/**
 * PwaUpdater — wires the service-worker update lifecycle to per-shell UX.
 *
 * Shell: any (mounted once at the app root in main.tsx). Renders nothing.
 *
 * On a new deployed build the waiting SW triggers `needRefresh`; we then apply
 * {@link applyPwaUpdate}: the kiosk reloads itself silently (no operator to
 * click), while desktop/mobile staff get a Mantine "New version — refresh"
 * notification that updates only on click, so an in-progress form isn't
 * interrupted.
 *
 * Registration is owned here (vite.config sets `injectRegister: false`). The
 * service worker only exists in production builds (`devOptions.enabled: false`),
 * so under `vite dev` / vitest `useRegisterSW` is an inert no-op.
 *
 * Intentionally NOT exported from the components barrel — keeping the
 * `virtual:pwa-register/react` import off the general module graph means vitest
 * never has to resolve a Vite-only virtual module.
 */
import { useEffect } from 'react';
import { useRegisterSW } from 'virtual:pwa-register/react';
import { notifications } from '@mantine/notifications';
import { Button, Group, Text } from '@mantine/core';
import { detectShell } from '../shells';
import { applyPwaUpdate } from '../lib/pwaUpdate';

const UPDATE_NOTIFICATION_ID = 'pwa-update-available';

export function PwaUpdater() {
  const {
    needRefresh: [needRefresh],
    updateServiceWorker,
  } = useRegisterSW();

  useEffect(() => {
    if (!needRefresh) return;
    applyPwaUpdate({
      shell: detectShell(window.location.pathname),
      updateServiceWorker,
      notifyRefresh: () =>
        notifications.show({
          id: UPDATE_NOTIFICATION_ID,
          title: 'New version available',
          color: 'brand',
          autoClose: false,
          withCloseButton: true,
          message: (
            <Group justify="space-between" wrap="nowrap" gap="sm" mt={4}>
              <Text size="sm">Refresh to load the latest Storefront update.</Text>
              <Button size="xs" onClick={() => void updateServiceWorker(true)}>
                Refresh
              </Button>
            </Group>
          ),
        }),
    });
  }, [needRefresh, updateServiceWorker]);

  return null;
}
